import React, { useState, useEffect, useRef } from 'react';
import { useClients, useDeleteClient, useRestoreClient } from '../../lib/hooks/useApi';
import { Client } from '../../lib/types/api';
import { ClientForm } from './ClientForm';
import { useConfirmation } from '../ui/ConfirmationDialog';
import { useToast } from '../ui/Toast';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  RotateCcw, 
  Eye,
  X,
  ChevronDown,
  ChevronUp,
  Building2,
  Users,
  Mail,
  MapPin,
  Calendar,
  MoreVertical,
  TrendingUp,
  Activity,
  Sparkles
} from 'lucide-react';
import { apiClient } from '../../lib/api/client';
import { useAuth } from '../../contexts/AuthContext';

interface ClientListProps {
  onClientSelect?: (client: Client) => void;
  showSelectButton?: boolean;
}

interface ClientRowProps {
  client: any;
  index: number;
  onEdit: (client: any) => void;
  onDelete: (client: any) => void;
  onRestore: (client: any) => void;
  onSelect?: (client: any) => void;
}

const ClientRow: React.FC<ClientRowProps> = ({ client, index, onEdit, onDelete, onRestore, onSelect }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isMenuOpen]);

  return (
    <tr 
      className="group hover:bg-glass-surface/30 transition-colors duration-200 animate-fade-in-up"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Combined Cell: Client Name, Contact Person, Email */}
      <td className="px-6 py-4">
        <div className="flex items-start space-x-3">
          <div className="p-2 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
            <Building2 className="w-5 h-5 text-primary-400" />
          </div>
          <div className="space-y-1 min-w-0">
            <div className="font-semibold text-text-enterprise-primary group-hover:text-primary-400 transition-colors duration-200">
              {client.client_name}
            </div>
            <div className="flex items-center space-x-2 text-sm text-text-enterprise-secondary">
              <Users className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{client.spoc_name}</span>
              {client.spocs && client.spocs.length > 0 && (
                <span className="text-xs text-text-enterprise-tertiary whitespace-nowrap">+{client.spocs.length}</span>
              )}
            </div>
            {client.contact_email && (
              <div className="flex items-center space-x-2 text-sm text-text-enterprise-tertiary">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{client.contact_email}</span>
              </div>
            )}
            {client.address?.city && (
              <div className="flex items-center space-x-2 text-xs text-text-enterprise-tertiary">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span>{client.address.city}</span>
              </div>
            )}
          </div>
        </div>
      </td>
      
      {/* Status */}
      <td className="px-6 py-4">
        <span
          className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
            client.status === 'active'
              ? 'bg-success-500/20 text-success-400 border border-success-500/30'
              : 'bg-surface-200/50 text-text-enterprise-tertiary border border-glass-border/30'
          }`}
        >
          <div className={`w-2 h-2 rounded-full mr-2 ${
            client.status === 'active' ? 'bg-success-400 animate-pulse' : 'bg-text-enterprise-tertiary'
          }`}></div>
          {client.status}
        </span>
      </td>
      
      {/* Created By */}
      <td className="px-6 py-4">
        <div className="text-text-enterprise-secondary text-sm">
          {client.created_by_name || 'System'}
        </div>
      </td>
      
      {/* Created Date */}
      <td className="px-6 py-4 text-text-enterprise-secondary text-sm whitespace-nowrap">
        {new Date(client.created_at).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })}
      </td>
      
      {/* Actions */}
      <td className="px-6 py-4">
        <div className="flex items-center justify-end space-x-2">
          {/* Action Dropdown Menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-text-enterprise-tertiary hover:text-text-enterprise-primary hover:bg-glass-surface/50 rounded-lg transition-all duration-200"
            >
              <MoreVertical className="w-5 h-5" />
            </button>
            
            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white/98 dark:bg-[#1A1A1A]/98 backdrop-blur-xl border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl shadow-black/40 z-50 overflow-hidden animate-fade-in-up">
                <div className="py-1">
                  {onSelect && (
                    <button
                      onClick={() => {
                        onSelect(client);
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150"
                    >
                      <Eye className="w-4 h-4 text-primary-400" />
                      <span>View Details</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => {
                      onEdit(client);
                      setIsMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150"
                  >
                    <Edit className="w-4 h-4 text-primary-400" />
                    <span>Edit Client</span>
                  </button>
                  
                  {client.deleted_at ? (
                    <button
                      onClick={() => {
                        onRestore(client);
                        setIsMenuOpen(false);
                      }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-150"
                    >
                      <RotateCcw className="w-4 h-4 text-success-400" />
                      <span>Restore Client</span>
                    </button>
                  ) : (
                    <>
                      <div className="h-px bg-gray-200 dark:bg-gray-700 my-1"></div>
                      <button
                        onClick={() => {
                          onDelete(client);
                          setIsMenuOpen(false);
                        }}
                        className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-error-400 hover:bg-error-500/20 dark:hover:bg-error-500/30 transition-colors duration-150"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete Client</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};

export const ClientList: React.FC<ClientListProps> = ({
  onClientSelect,
  showSelectButton = false,
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isLoaded, setIsLoaded] = useState(false);

  // Enterprise-level UI hooks
  const { confirm } = useConfirmation();
  const { toast } = useToast();
  const { user } = useAuth();

  // Animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const { data: clientsData, isLoading, error } = useClients({
    search: search || undefined,
    status: statusFilter || undefined,
    page,
    size: pageSize,
  });

  const deleteClient = useDeleteClient();
  const restoreClient = useRestoreClient();

  const clients = clientsData?.clients || [];
  const total = clientsData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  const handleDelete = async (client: Client) => {
    confirm({
      title: 'Delete Client',
      description: `Are you sure you want to delete "${client.client_name}"? This action will soft delete the client and can be restored later.`,
      variant: 'destructive',
      confirmText: 'Delete Client',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await deleteClient.mutateAsync(client.id);
          toast({
            title: 'Client Deleted',
            description: `"${client.client_name}" has been successfully deleted.`,
            variant: 'success',
            duration: 5000
          });
        } catch (error) {
          console.error('Error deleting client:', error);
          toast({
            title: 'Delete Failed',
            description: 'Failed to delete client. Please try again.',
            variant: 'error',
            duration: 7000
          });
        }
      }
    });
  };

  const handleRestore = async (client: Client) => {
    try {
      await restoreClient.mutateAsync(client.id);
      toast({
        title: 'Client Restored',
        description: `"${client.client_name}" has been successfully restored.`,
        variant: 'success',
        duration: 5000
      });
    } catch (error) {
      console.error('Error restoring client:', error);
      toast({
        title: 'Restore Failed',
        description: 'Failed to restore client. Please try again.',
        variant: 'error',
        duration: 7000
      });
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingClient(null);
    toast({
      title: editingClient ? 'Client Updated' : 'Client Created',
      description: editingClient 
        ? 'The client has been successfully updated.' 
        : 'The client has been successfully created.',
      variant: 'success',
      duration: 5000
    });
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleCreateNew = () => {
    setEditingClient(null);
    setShowForm(true);
  };

  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState<Array<{ row: number; client_name: string; status: string; message?: string }>>([]);
  const [bulkProgress, setBulkProgress] = useState<number>(0);

  const handleDownloadClientsTemplate = async () => {
    try {
      const blob = await apiClient.downloadClientsTemplateCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'clients_bulk_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({ title: 'Failed to download template', description: e?.message, variant: 'error' });
    }
  };

  const onBulkFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkResults([]);
    setBulkProgress(0);
    try {
      setBulkUploading(true);
      // Fake progress since processing is synchronous server-side
      const progressTimer = setInterval(() => setBulkProgress((p) => Math.min(95, p + 5)), 150);
      const res = await apiClient.bulkUploadClients(file);
      clearInterval(progressTimer);
      setBulkProgress(100);
      setBulkResults(res.results || []);
      toast({
        title: 'Bulk upload completed',
        description: `Processed: ${res.processed}, Created: ${res.created}, Skipped: ${res.skipped_duplicates}, Errors: ${res.errors}`,
        variant: res.errors > 0 ? 'warning' : 'success',
        duration: 7000
      });
    } catch (err: any) {
      setBulkProgress(0);
      toast({
        title: 'Bulk upload failed',
        description: err?.response?.data?.detail || 'Failed to process CSV',
        variant: 'error',
        duration: 7000
      });
    } finally {
      setBulkUploading(false);
      e.target.value = '';
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  const activeFiltersCount = [search, statusFilter].filter(Boolean).length;

  if (showForm) {
    return (
      <div className="glass-card p-8 animate-fade-in-up">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-xl">
              <Building2 className="w-6 h-6 text-primary-400" />
            </div>
            <h2 className="text-2xl font-bold text-text-enterprise-primary">
              {editingClient ? 'Edit Client' : 'Create New Client'}
            </h2>
          </div>
          <button
            onClick={() => setShowForm(false)}
            className="p-2 text-text-enterprise-tertiary hover:text-text-enterprise-primary hover:bg-glass-surface/50 rounded-xl transition-all duration-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <ClientForm
          editData={editingClient}
          mode={editingClient ? 'edit' : 'create'}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowForm(false)}
        />
      </div>
    );
  }

  return (
    <div className={`space-y-8 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {/* Liquid Glassmorphic Header */}
      <div className="relative">
        <div className="glass-card p-8 relative overflow-hidden">
          {/* Animated Background Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary-500/10 to-secondary-500/5 rounded-full blur-2xl animate-liquid-flow"></div>
            <div className="absolute -bottom-20 -left-20 w-32 h-32 bg-gradient-to-tr from-secondary-500/10 to-primary-500/5 rounded-full blur-2xl animate-liquid-flow" style={{ animationDelay: '2s' }}></div>
          </div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-2xl animate-pulse-glow">
                  <Building2 className="w-8 h-8 text-primary-400" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-text-enterprise-primary via-text-enterprise-accent to-text-enterprise-primary bg-clip-text text-transparent">
                    Client Management
                  </h1>
                  <p className="text-text-enterprise-secondary text-lg">Manage client companies and contacts with precision</p>
                </div>
              </div>
              
              {/* Stats Cards */}
              <div className="flex space-x-4 mt-6">
                <div className="glass-card p-4 min-w-[120px]">
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-primary-400" />
                    <span className="text-text-enterprise-secondary text-sm">Total</span>
                  </div>
                  <div className="text-2xl font-bold text-text-enterprise-primary mt-1">{total}</div>
                </div>
                <div className="glass-card p-4 min-w-[120px]">
                  <div className="flex items-center space-x-2">
                    <Activity className="w-5 h-5 text-success-400" />
                    <span className="text-text-enterprise-secondary text-sm">Active</span>
                  </div>
                  <div className="text-2xl font-bold text-text-enterprise-primary mt-1">
                    {clients.filter((c: Client) => c.status === 'active').length}
                  </div>
                </div>
                <div className="glass-card p-4 min-w-[120px]">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-warning-400" />
                    <span className="text-text-enterprise-secondary text-sm">Growth</span>
                  </div>
                  <div className="text-2xl font-bold text-text-enterprise-primary mt-1">+12%</div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreateNew}
                className="group relative overflow-hidden bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 border border-primary-500/30 text-text-enterprise-primary px-6 py-3 rounded-2xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-glow-primary hover:border-primary-500/50"
              >
                <div className="flex items-center space-x-2 relative z-10">
                  <Plus className="w-5 h-5" />
                  <span>Create Client</span>
                  <Sparkles className="w-4 h-4 animate-pulse" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>

              {(user?.role === 'admin' || user?.scopes?.includes('all:admin')) && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDownloadClientsTemplate}
                    className="px-4 py-3 rounded-2xl border border-glass-border/40 text-text-enterprise-primary hover:border-primary-500/40 hover:shadow-glow-primary transition-all"
                  >
                    Download Template
                  </button>
                  <label className={`px-4 py-3 rounded-2xl border border-glass-border/40 text-text-enterprise-primary hover:border-primary-500/40 hover:shadow-glow-primary transition-all cursor-pointer ${bulkUploading ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {bulkUploading ? `Uploading… ${bulkProgress}%` : 'Bulk Upload CSV'}
                    <input type="file" accept=".csv" className="hidden" onChange={onBulkFileChange} disabled={bulkUploading} />
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Liquid Glassmorphic Search and Filters */}
      <div className="glass-card p-6 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary-500/5 to-transparent rounded-full blur-xl animate-floating"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-secondary-500/5 to-transparent rounded-full blur-xl animate-floating" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="relative z-10 flex flex-col lg:flex-row items-center space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-2xl blur-sm group-hover:blur-md transition-all duration-300"></div>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-enterprise-tertiary w-5 h-5 group-hover:text-primary-400 transition-colors duration-300" />
              <input
                type="text"
                placeholder="Search clients by name, email, or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-glass-surface/60 border border-glass-border/40 rounded-2xl text-text-enterprise-primary placeholder-text-enterprise-muted focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 focus:bg-glass-surface/80 transition-all duration-300 backdrop-blur-xl"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/10 to-primary-500/10 rounded-2xl blur-sm group-hover:blur-md transition-all duration-300"></div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="relative px-4 py-3 bg-glass-surface/60 border border-glass-border/40 rounded-2xl text-text-enterprise-primary focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 focus:bg-glass-surface/80 transition-all duration-300 backdrop-blur-xl min-w-[140px]"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="group relative overflow-hidden flex items-center space-x-2 px-4 py-3 text-text-enterprise-secondary hover:text-text-enterprise-primary border border-glass-border/40 rounded-2xl hover:bg-glass-surface/50 hover:border-primary-500/30 transition-all duration-300 backdrop-blur-xl"
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <span className="px-2 py-1 text-xs bg-primary-500/20 text-primary-400 rounded-full">
                {activeFiltersCount}
              </span>
            )}
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
          </button>

          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <button
              onClick={clearFilters}
              className="px-4 py-3 text-sm text-text-enterprise-tertiary hover:text-text-enterprise-primary hover:bg-glass-surface/50 rounded-2xl transition-all duration-300"
            >
              Clear All
            </button>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-glass-border/30 animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-enterprise-secondary mb-2">
                  Results Per Page
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="w-full px-4 py-3 bg-glass-surface/60 border border-glass-border/40 rounded-2xl text-text-enterprise-primary focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 focus:bg-glass-surface/80 transition-all duration-300 backdrop-blur-xl"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="glass-card p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="w-5 h-5 text-primary-400" />
            <span className="text-text-enterprise-secondary">
              Showing <span className="font-semibold text-text-enterprise-primary">{clients.length}</span> of <span className="font-semibold text-text-enterprise-primary">{total}</span> clients
            </span>
          </div>
          <div className="flex items-center space-x-2 text-text-enterprise-tertiary">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {/* Bulk upload per-row statuses */}
      {user?.role === 'admin' && bulkResults.length > 0 && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold text-text-enterprise-primary mb-3">Bulk Upload Results</h3>
          <div className="max-h-64 overflow-auto border border-glass-border/30 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-glass-surface/50">
                <tr>
                  <th className="text-left px-3 py-2">Row</th>
                  <th className="text-left px-3 py-2">Client</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Message</th>
                </tr>
              </thead>
              <tbody>
                {bulkResults.map((r, i) => (
                  <tr key={i} className="border-t border-glass-border/20">
                    <td className="px-3 py-2">{r.row}</td>
                    <td className="px-3 py-2">{r.client_name}</td>
                    <td className={`px-3 py-2 font-medium ${r.status === 'CREATED' ? 'text-success-500' : r.status === 'SKIPPED_DUPLICATE' ? 'text-warning-500' : 'text-error-500'}`}>{r.status}</td>
                    <td className="px-3 py-2 text-text-enterprise-secondary">{r.message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="glass-card p-12">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="w-12 h-12 border-4 border-primary-500/20 border-t-primary-500 rounded-full animate-spin"></div>
              <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-secondary-500/30 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Loading Clients</h3>
              <p className="text-text-enterprise-secondary">Please wait while we fetch your data...</p>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="glass-card p-6 border border-error-500/30 bg-gradient-to-br from-error-500/10 to-error-600/5">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-error-500/20 rounded-xl">
              <X className="w-6 h-6 text-error-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-enterprise-primary">Error Loading Clients</h3>
              <p className="text-text-enterprise-secondary">{error.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modern DataTable */}
      {!isLoading && !error && clients.length > 0 && (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-glass-border/30 bg-glass-surface/30">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-text-enterprise-secondary uppercase tracking-wider">
                    Client Details
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-text-enterprise-secondary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-text-enterprise-secondary uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-semibold text-text-enterprise-secondary uppercase tracking-wider">
                    Created
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-text-enterprise-secondary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-glass-border/20">
                {clients.map((client: any, index: number) => (
                  <ClientRow
                    key={client.id}
                    client={client}
                    index={index}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onRestore={handleRestore}
                    onSelect={showSelectButton ? onClientSelect : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Liquid Glassmorphic Pagination */}
      {totalPages > 1 && (
        <div className="glass-card p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-text-enterprise-secondary">
              <Activity className="w-4 h-4" />
              <span className="text-sm">
                Page {page} of {totalPages} • {total} total clients
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="group relative overflow-hidden px-4 py-2 text-text-enterprise-primary border border-glass-border/40 rounded-xl hover:shadow-glow-primary hover:border-primary-500/30 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-300 backdrop-blur-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="relative z-10">Previous</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`relative overflow-hidden px-3 py-2 text-sm font-medium rounded-lg transition-all duration-300 ${
                        pageNum === page
                          ? 'bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border border-primary-500/30 text-text-enterprise-primary shadow-glow-primary'
                          : 'text-text-enterprise-tertiary hover:text-text-enterprise-primary hover:bg-glass-surface/50 border border-glass-border/30'
                      }`}
                    >
                      <span className="relative z-10">{pageNum}</span>
                      {pageNum === page && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="group relative overflow-hidden px-4 py-2 text-text-enterprise-primary border border-glass-border/40 rounded-xl hover:shadow-glow-primary hover:border-primary-500/30 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-300 backdrop-blur-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="relative z-10">Next</span>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liquid Glassmorphic Empty State */}
      {!isLoading && !error && clients.length === 0 && (
        <div className="glass-card p-12 text-center relative overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-primary-500/10 to-secondary-500/5 rounded-full blur-2xl animate-liquid-flow"></div>
            <div className="absolute -bottom-20 -left-20 w-32 h-32 bg-gradient-to-tr from-secondary-500/10 to-primary-500/5 rounded-full blur-2xl animate-liquid-flow" style={{ animationDelay: '2s' }}></div>
          </div>
          
          <div className="relative z-10">
            <div className="p-4 bg-gradient-to-br from-primary-500/20 to-secondary-500/20 rounded-2xl w-fit mx-auto mb-6">
              <Building2 className="w-12 h-12 text-primary-400" />
            </div>
            <h3 className="text-2xl font-bold text-text-enterprise-primary mb-3">
              {activeFiltersCount > 0 ? 'No clients found' : 'No clients yet'}
            </h3>
            <p className="text-text-enterprise-secondary mb-8 max-w-md mx-auto">
              {activeFiltersCount > 0
                ? 'Try adjusting your search or filter criteria to find what you\'re looking for.'
                : 'Get started by creating your first client and begin managing your business relationships.'}
            </p>
            {activeFiltersCount === 0 && (
              <button
                onClick={handleCreateNew}
                className="group relative overflow-hidden bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 border border-primary-500/30 text-text-enterprise-primary px-6 py-3 rounded-2xl font-semibold transition-all duration-300 hover:scale-105 hover:shadow-glow-primary hover:border-primary-500/50"
              >
                <div className="flex items-center space-x-2 relative z-10">
                  <Plus className="w-5 h-5" />
                  <span>Create Your First Client</span>
                  <Sparkles className="w-4 h-4 animate-pulse" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
